import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, CalendarClock, Users, Sparkles, Search } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { MeetingFormDialog } from "./MeetingFormDialog";
import { MeetingRoom } from "./MeetingRoom";
import { fmtWhen, STATUS, MEET_COLORS, nowLocal } from "./bizUtils";

export const useMeetingDialogs = (reload) => {
  const [form, setForm] = useState(null);
  const [room, setRoom] = useState(null);
  const [rev, setRev] = useState(0);
  const ui = (employees) => (<>
    <MeetingFormDialog open={!!form} initial={form?.id || form?.start || form?.date ? form : null} employees={employees} onClose={() => setForm(null)}
      onSaved={(r) => { reload(); setRev((x) => x + 1); if (!form?.id && r?.id) setRoom(r.id); }} />
    <MeetingRoom key={`${room}-${rev}`} id={room} onClose={() => setRoom(null)} onEdit={(m) => setForm(m)} onChanged={reload} />
  </>);
  return { setForm, setRoom, ui };
};

const Card = ({ m, onOpen }) => {
  const st = STATUS[m.status] || STATUS.scheduled;
  return (
    <button onClick={() => onOpen(m.id)} data-testid={`meeting-card-${m.id}`}
      className="group flex w-full items-stretch gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 text-right transition-shadow hover:shadow-md">
      <span className={`w-1 shrink-0 rounded-full ${MEET_COLORS[m.color] || MEET_COLORS.blue}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2"><p className="font-heading font-bold text-slate-900">{m.title}</p>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${st[1]}`}>{st[0]}</span>
          {m.kind === "event" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">موعد</span>}
          {m.kind === "video" && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">اجتماع مرئي</span>}
          {m.summary && <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-bold text-violet-700"><Sparkles className="h-3 w-3" />محضر</span>}</div>
        <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-slate-500"><span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />{fmtWhen(m.start)}</span>
          <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{(m.attendees || []).length} حضور</span>
          {(m.task_ids || []).length > 0 && <span>{m.task_ids.length} مهام</span>}</p>
      </div>
    </button>
  );
};

export const MeetingsPanel = ({ employees = [] }) => {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [q, setQ] = useState("");
  const load = useCallback(() => api.meetings().then((r) => setRows(r.items)).catch(() => { setRows([]); toast.error("تعذّر تحميل الاجتماعات"); }), []);
  useEffect(() => { load(); }, [load]);
  const { setForm, setRoom, ui } = useMeetingDialogs(load);
  const now = nowLocal();
  const list = useMemo(() => (rows || []).filter((m) => (!q.trim() || m.title.includes(q.trim()))
    && (tab === "upcoming" ? m.status === "scheduled" && (m.end || m.start) >= now : tab === "past" ? m.status === "done" || (m.status !== "cancelled" && (m.end || m.start) < now) : m.status === "cancelled"))
    .sort((a, b) => (tab === "upcoming" ? a.start.localeCompare(b.start) : b.start.localeCompare(a.start))), [rows, tab, q, now]);
  const kpi = { up: (rows || []).filter((m) => m.status === "scheduled" && (m.end || m.start) >= now).length, done: (rows || []).filter((m) => m.status === "done").length,
    tasks: (rows || []).reduce((s, m) => s + (m.task_ids || []).length, 0) };
  return (
    <div className="space-y-6" data-testid="meetings-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="font-heading text-2xl font-extrabold text-slate-900">الاجتماعات</h2>
          <p className="mt-1 text-sm text-slate-500">جدولة، تسجيل وتفريغ، محضر ذكي، ومهام تلقائية للمسؤولين.</p></div>
        <Button onClick={() => setForm({})} className="gap-1.5 rounded-xl" data-testid="meeting-new"><Plus className="h-4 w-4" /> اجتماع جديد</Button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[["القادمة", kpi.up, "text-blue-600"], ["المنتهية", kpi.done, "text-emerald-600"], ["مهام من الاجتماعات", kpi.tasks, "text-violet-600"]].map(([l, v, c]) => (
          <div key={l} className="rounded-2xl border border-slate-200/70 bg-white p-4"><p className="text-xs text-slate-500">{l}</p><p className={`mt-1 font-heading text-2xl font-extrabold ${c}`}>{v}</p></div>))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1 text-sm">{[["upcoming", "القادمة"], ["past", "السابقة"], ["cancelled", "الملغاة"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`meetings-tab-${k}`} className={`rounded-lg px-3 py-1.5 ${tab === k ? "bg-white font-bold shadow-sm" : "text-slate-500"}`}>{l}</button>))}</div>
        <div className="relative ms-auto w-full sm:w-64"><Search className="absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث…" className="pr-8" data-testid="meetings-search" /></div>
      </div>
      {!rows ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        : list.length === 0 ? <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-400" data-testid="meetings-empty">لا توجد اجتماعات هنا</div>
          : <div className="grid gap-2.5 lg:grid-cols-2">{list.map((m) => <Card key={m.id} m={m} onOpen={setRoom} />)}</div>}
      {ui(employees)}
    </div>
  );
};
